import { useState } from 'react'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

/** A YYYY-MM-DD day as a local Date, so the picker shows that calendar day
 *  whatever the viewer's time zone. */
function toLocalDay(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1)
}

function toIsoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

interface DayFieldProps {
  id: string
  value: string
  /** The earliest day that can be picked, YYYY-MM-DD. */
  earliestDay: string
  disabled?: boolean
  onChange: (value: string) => void
}

/** Picks a day from `earliestDay` on: earlier days in its month are
 *  disabled, and earlier months cannot be reached. Billing's credit and rate
 *  card dialogs both date with it. */
export function DayField({
  id,
  value,
  earliestDay,
  disabled = false,
  onChange,
}: DayFieldProps) {
  const [open, setOpen] = useState(false)
  const earliest = toLocalDay(earliestDay)
  const selected = toLocalDay(value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal">
          {selected.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
          <CalendarIcon className="size-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          required
          selected={selected}
          defaultMonth={selected}
          startMonth={earliest}
          disabled={{ before: earliest }}
          showOutsideDays={false}
          onSelect={(date) => {
            onChange(toIsoDay(date))
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
